import type {
  Dictionary,
  EnumParameter as EnumParameterType,
  FloatParameter,
  ScanServiceParameter,
  StringParameter,
} from '@witmetrics/utils';
import { PARAMETER_TYPES } from '@witmetrics/utils/constants';
import type { FloatFieldValue } from '@witmetrics/unison-core';
import TextField from '@/components/inputs/TextField';
import NumberInput from '@/components/inputs/NumberInput';
import ParameterCard from './ParameterCard';
import EnumParameter from './EnumParameter';
import BooleanEnumParameter from './BooleanEnumParameter';
import type { ServiceParameterValue } from './types';
import { classes, isBooleanEnum } from './utils';

export type ServiceParameterProps = {
  parameter: ScanServiceParameter;
  value: ServiceParameterValue;
  increment?: number;
  onChange: (update: Dictionary<ServiceParameterValue>) => void;
};

export default function ServiceParameter({
  parameter,
  value,
  increment = 0.1,
  onChange,
}: ServiceParameterProps) {
  const handleChange = (newValue: ServiceParameterValue) => {
    return onChange({ [parameter.name]: newValue });
  };

  switch (parameter.type) {
    case PARAMETER_TYPES.ENUM: {
      const enumParam = parameter as EnumParameterType;
      if (isBooleanEnum(enumParam)) {
        return (
          <BooleanEnumParameter
            parameter={enumParam}
            value={value as string | null}
            onChange={handleChange}
          />
        );
      }
      return (
        <EnumParameter
          parameter={enumParam}
          value={value as string | null}
          onChange={handleChange}
        />
      );
    }
    case PARAMETER_TYPES.STRING: {
      const stringParam = parameter as StringParameter;
      return (
        <ParameterCard
          bodyClassName={classes.inputBody}
          description={stringParam.description}
          label={stringParam.label}>
          <TextField
            className={classes.input}
            label=""
            placeholder={stringParam.label}
            value={(value as string) || ''}
            onChange={handleChange}
          />
        </ParameterCard>
      );
    }
    case PARAMETER_TYPES.FLOAT: {
      const floatParam = parameter as FloatParameter;
      return (
        <ParameterCard
          bodyClassName={classes.inputBody}
          description={floatParam.description}
          label={floatParam.label}>
          <NumberInput
            className={classes.input}
            increment={increment}
            label=""
            min={0}
            name={floatParam.name}
            size="small"
            value={value as FloatFieldValue}
            onChange={handleChange}
          />
        </ParameterCard>
      );
    }
    case PARAMETER_TYPES.JAW_OUTLINE: {
      console.warn('Jaw outline parameter type is not yet implemented');
      return null;
    }
    case PARAMETER_TYPES.LIST: {
      console.warn('List parameter type is not yet implemented');
      return null;
    }
    default: {
      throw new Error(
        `Unhandled ServiceParameter type: ${(parameter as ScanServiceParameter).type}`
      );
    }
  }
}
